import React from "react"
import { TreePine, Plus, X } from "lucide-react"
import { TreeEntry } from "../types"

interface TreeInventoryProps {
  trees: TreeEntry[]
  treeInput: string
  treeTypes: Array<{id: number, name: string}>
  onInputChange: (v: string) => void
  onAdd: (name: string) => void
  onRemove: (id: string) => void
  onUpdate: (id: string, key: keyof TreeEntry, value: string) => void
}

export const TreeInventory: React.FC<TreeInventoryProps> = ({
  trees, treeInput, treeTypes, onInputChange, onAdd, onRemove, onUpdate,
}) => (
  <div className="border-b border-neutral-600 pb-3">

    {/* Quick-add suggestions */}
    <div className="flex flex-wrap gap-1.5 mb-3">
      {treeTypes.map(t => (
        <button
          key={t.id}
          type="button"
          onClick={() => onAdd(t.name)}
          disabled={!!trees.find(x => x.name === t.name)}
          className="text-[10px] px-2.5 py-1 rounded-full border border-neutral-700 text-neutral-400 hover:border-emerald-500/50 hover:text-emerald-400 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          + {t.name}
        </button>
      ))}
    </div>

    {/* Custom input */}
    <div className="flex gap-2">
      <input
        className="flex-1 bg-transparent text-white text-xs outline-none placeholder:text-neutral-600"
        placeholder="Type tree name and press Enter..."
        value={treeInput}
        onChange={e => onInputChange(e.target.value)}
        onKeyDown={e => {
          if (e.key === "Enter") {
            e.preventDefault()
            onAdd(treeInput)
          }
        }}
      />
      <button
        type="button"
        onClick={() => onAdd(treeInput)}
        className="text-emerald-400 hover:text-emerald-300 transition-colors"
      >
        <Plus className="w-4 h-4" />
      </button>
    </div>

    {/* Tree rows */}
    {trees.length > 0 && (
      <div className="mt-3 space-y-2">
        {trees.map(tree => (
          <div key={tree.id} className="flex items-center gap-3 bg-white/5 rounded-lg px-3 py-2">
            <TreePine className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
            <span className="text-xs text-white flex-1 font-medium">{tree.name}</span>
            <input
              type="number"
              placeholder="Count"
              min={0}
              value={tree.count}
              onChange={e => onUpdate(tree.id, "count", e.target.value)}
              className="w-16 bg-transparent border-b border-neutral-600 text-white text-xs text-center outline-none focus:border-emerald-400 transition-colors"
            />
            <select
              value={tree.condition}
              onChange={e => onUpdate(tree.id, "condition", e.target.value)}
              className="bg-transparent text-neutral-400 text-[10px] outline-none cursor-pointer"
            >
              <option value="good" className="bg-[#1e1e1e]">Good</option>
              <option value="fair" className="bg-[#1e1e1e]">Fair</option>
              <option value="poor" className="bg-[#1e1e1e]">Poor</option>
            </select>
            <button
              type="button"
              onClick={() => onRemove(tree.id)}
              className="text-neutral-600 hover:text-red-400 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
    )}
  </div>
  )
